import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocaleSwitcher } from "@/components/layout/locale-switcher";
import { useAuth } from "@/auth/auth-provider";
import { authErrorMessage } from "@/auth/errors";

const schema = z.object({
  email: z
    .string()
    .min(1, "auth.signIn.errors.emailRequired")
    .email("auth.signIn.errors.emailInvalid"),
});
type Values = z.infer<typeof schema>;

const searchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute("/forgot-password")({
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: search.email ?? "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await requestPasswordReset(values.email);
      navigate({
        to: "/reset-password",
        search: { email: values.email },
      });
    } catch (err) {
      setServerError(authErrorMessage(err));
    }
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-6">
      <div className="absolute top-4 right-4">
        <LocaleSwitcher align="end" />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <img src="/logo.svg" alt="MStar" className="h-8 w-auto mb-2 text-foreground" />
          <CardTitle className="text-xl">{t("auth.forgot.title")}</CardTitle>
          <CardDescription>{t("auth.forgot.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.forgot.email")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                {...form.register("email")}
                aria-invalid={!!form.formState.errors.email}
              />
              {form.formState.errors.email ? (
                <p className="text-xs text-destructive">
                  {t(form.formState.errors.email.message ?? "")}
                </p>
              ) : null}
            </div>

            {serverError ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {serverError}
              </div>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t("auth.forgot.submitting")}
                </>
              ) : (
                t("auth.forgot.submit")
              )}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                {t("auth.forgot.backToSignIn")}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
